import "@fontsource/poppins/latin-400.css";
import "@fontsource/poppins/latin-500.css";
import "@fontsource/poppins/latin-600.css";
import "@fontsource/poppins/latin-700.css";
import "@fontsource/jetbrains-mono/latin-400.css";
import "@fontsource/jetbrains-mono/latin-500.css";
import "./styles.css";

import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate } from "react-router";
import type { Permission } from "./api/types.ts";
import { IconAlert } from "./components/Icons.tsx";
import { ToastProvider } from "./components/Toast.tsx";
import { fmt, I18nProvider, tr, useT } from "./i18n/index.tsx";
import { EmptyState } from "./components/ui.tsx";
import { AppShell, NAV } from "./layout/AppShell.tsx";
import { SessionProvider, useSession } from "./lib/session.tsx";
import { Settings } from "./pages/Settings.tsx";
import { Statement } from "./pages/Statement.tsx";
import { Vault } from "./pages/Vault.tsx";
import { Activity } from "./pages/Activity.tsx";
import { Deposits } from "./pages/Deposits.tsx";
import { Developers } from "./pages/Developers.tsx";
import { ApplyVerify } from "./pages/ApplyVerify.tsx";
import { Invite } from "./pages/Invite.tsx";
import { Login } from "./pages/Login.tsx";
import { Pay } from "./pages/Pay.tsx";
import { NotFound } from "./pages/NotFound.tsx";
import { Overview } from "./pages/Overview.tsx";
import { Payouts } from "./pages/Payouts.tsx";
import { Security } from "./pages/Security.tsx";
import { Help } from "./pages/Help.tsx";
import { Team } from "./pages/Team.tsx";
import { logo, mark, warmImages } from "./assets/images.ts";
import { installAppFeel } from "./lib/app-feel.ts";

installAppFeel();

function Root() {
  return (
    <SessionProvider>
      <ToastProvider>
        <BlockedGate />
      </ToastProvider>
    </SessionProvider>
  );
}

/** 403 ip_not_allowed from any call → a clean full-page state instead of half-broken pages. */
function BlockedGate() {
  const t = useT();
  const { blocked, logout } = useSession();
  const navigate = useNavigate();
  if (!blocked) return <Outlet />;
  return (
    <div className="blocked">
      <div className="blocked-card">
        <img src={logo} alt="iCryptoNow" width={176} height={30} className="blocked-logo" />
        <div className="blocked-icon"><IconAlert size={22} /></div>
        <h1>{t.shell.blockedTitle}</h1>
        <p className="lead-sm">{blockedDetail(blocked)}</p>
        <p className="muted small">{t.shell.blockedBody}</p>
        <button type="button" className="btn btn-primary" onClick={async () => { await logout().catch(() => {}); navigate("/login", { replace: true }); }}>
          {t.shell.logOut}
        </button>
      </div>
    </div>
  );
}

/** The server message minus the part the title already says: "access … not allowed; contact your account owner" → "Contact your account owner." */
function blockedDetail(msg: string) {
  const rest = msg.replace(/^access from this network is not allowed[;,.]?\s*/i, "").trim();
  const text = rest || tr().shell.blockedContactOwner;
  return text.charAt(0).toUpperCase() + text.slice(1) + (/[.!?]$/.test(text) ? "" : ".");
}

/** Page needs any of `perms` for the active brand. */
function Guard({ perms, children }: { perms: Permission[]; children: ReactNode }) {
  const t = useT();
  const { canAny, me } = useSession();
  if (canAny(...perms)) return <>{children}</>;
  return (
    <div className="page">
      <EmptyState title={t.shell.noAccessTitle} art>
        {fmt(t.shell.noAccessBody, { brand: me?.merchant.name ?? t.shell.thisBrand })}
      </EmptyState>
    </div>
  );
}

/** "/" → overview, or the first page the role can open. */
function Home() {
  const { canAny } = useSession();
  if (canAny("overview:view")) return <Overview />;
  const first = NAV.find((n) => canAny(...n.perms));
  return first ? <Navigate to={first.to} replace /> : <Guard perms={["overview:view"]}>{null}</Guard>;
}

function Splash() {
  const t = useT();
  return (
    <div className="splash" role="status" aria-label={t.common.loading}>
      <img src={mark} alt="" width={48} height={48} />
    </div>
  );
}

/** Logged-in area: no session → /login?next=<this page>. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <Splash />;
  if (!me) {
    const next = loc.pathname + loc.search;
    return <Navigate to={next === "/" ? "/login" : `/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    element: <Root />,
    children: [
      { path: "/login", element: <Login /> },
      { path: "/invite", element: <Invite /> },
      { path: "/apply/verify", element: <ApplyVerify /> },
      { path: "/sandbox/verify", element: <ApplyVerify sandbox /> },
      { path: "/pay/:token", element: <Pay /> },
      {
        element: (
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        ),
        children: [
          { index: true, element: <Home /> },
          { path: "deposits", element: <Guard perms={["deposits:view"]}><Deposits /></Guard> },
          { path: "payouts", element: <Guard perms={["payouts:view"]}><Payouts /></Guard> },
          { path: "statement", element: <Guard perms={["statement:view"]}><Statement /></Guard> },
          { path: "vault", element: <Guard perms={["vault:view"]}><Vault /></Guard> },
          { path: "developers", element: <Guard perms={["api_keys:view", "webhook:view"]}><Developers /></Guard> },
          { path: "team", element: <Guard perms={["team:view"]}><Team /></Guard> },
          { path: "activity", element: <Guard perms={["activity:view"]}><Activity /></Guard> },
          { path: "settings", element: <Guard perms={["settings:view"]}><Settings /></Guard> },
          { path: "settings/security", element: <Security /> },
          { path: "help", element: <Help /> },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
]);

createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <I18nProvider>
      <RouterProvider router={router} />
    </I18nProvider>
  </StrictMode>,
);

warmImages();
